import React from 'react';
import { FormControl, MenuItem, Select, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
} from '@material-ui/core';
import AppContext from 'contexts/AppContext';
import DropDown from 'components/DropDown';
import SearchBar from 'components/SearchBar';
import { ArrowUpward, Delete, FilterList, GetApp } from '@material-ui/icons';
import { getPaginatedUsers, exportUsers, destroyUser } from 'services/userService';
import type PaginationData from 'types/models/PaginationData';
import type User from 'types/models/User';
import type { UserFilterParams } from 'types/models/filters';
import 'styles/components/DataTable.scss';
import { parseDateTimeLumen } from 'utils/datetime';
import ConfirmDialog from 'components/ConfirmDialog';

export default (): JSX.Element => {
  const [showFilterDropDown, setShowFilterDropDown] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<string | null>(null);
  const { isLoading, setIsLoading } = React.useContext(AppContext);
  const [users, setUsers] = React.useState<PaginationData<User> | null>(null);
  const [userFilterParams, setUserFilterParams] = React.useState<UserFilterParams | null>(null);
  const [sortedField, setSortedField] = React.useState<{ field: string; sort: 'asc' | 'desc' }>({
    field: '',
    sort: 'asc',
  });
  const [withFilter, setWithFilter] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const userMenuItems = [
    { label: t('upper-fullname'), value: 'fullname' },
    { label: t('upper-email'), value: 'email' },
    { label: t('form-schemas.identification-number'), value: 'identificationNumber' },
    { label: t('form-schemas.upper-age-range'), value: 'ageRange' },
    { label: t('venue-code'), value: 'venueCode' },
    { label: t('seller-name'), value: 'sellerName' },
  ];

  const loadData = React.useCallback(async (page?: number, userFilterParams?: UserFilterParams | null) => {
    setIsLoading(true);
    const users = await getPaginatedUsers(page, userFilterParams);

    if (users) setUsers(users);

    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    loadData();
  }, []);

  const renderDeleteButton = (id: string): JSX.Element => (
    <IconButton
      color="inherit"
      onClick={() => {
        if (isLoading) return;
        if (!id) return;
        setUserToDelete(id);
        setOpenDialog(true);
      }}
    >
      <Delete />
    </IconButton>
  );

  const onCancel = (): void => {
    setOpenDialog(false);
  };

  const onConfirm = async (): Promise<void> => {
    await destroyUser(userToDelete ?? '');
    loadData(1, userFilterParams);
  };

  const getXlsxUsers = async () => {
    const data = await exportUsers(userFilterParams, withFilter);
    const a = document.createElement('a');

    if (data) {
      a.href = data;
      a.target = '_blank';
      a.click();
    }
  };

  const handleTableCellClick = async (field: string) => {
    const sort = sortedField.sort === 'asc' ? 'desc' : 'asc';
    setSortedField({ field, sort });
    const filters: UserFilterParams = { ...userFilterParams, sortBy: field, sortDir: sort };
    setUserFilterParams(filters);
    setWithFilter(true);
    loadData(1, filters);
  };

  const renderContent = (paginationData: User[] = []): JSX.Element => {
    return (
      <>
        {paginationData.map((user: User) => (
          <TableRow key={user.id}>
            <TableCell component="th" scope="row">
              {user.fullname ?? '-'}
            </TableCell>
            <TableCell component="th" scope="row">
              {user.email ?? '-'}
            </TableCell>
            <TableCell component="th" scope="row">
              {user.identificationNumber ?? '-'}
            </TableCell>
            <TableCell>{user.ageRange ?? '-'}</TableCell>
            <TableCell>{(user.countryPhoneCode + user.phone).toString() ?? '-'}</TableCell>
            <TableCell>{user.venue?.code ?? '-'}</TableCell>
            <TableCell>{user.ueMac ?? '-'}</TableCell>
            <TableCell>{user.ueIp ?? '-'}</TableCell>
            <TableCell>{user.seller?.name ?? '-'}</TableCell>
            <TableCell>
              <div className="Action--buttons">{renderDeleteButton(user.id)}</div>
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <section className="Table--header">
        <h3>{t('all-users')}</h3>
      </section>

      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <div className="Table--wrapper">
        <div className="Filter--wrapper">
          <Button
            size="small"
            variant="contained"
            color="primary"
            className="Filter--button"
            onClick={() => setShowFilterDropDown(!showFilterDropDown)}
          >
            <span className="noabbr">
              <FilterList />
              <p>{t('filter')}</p>
            </span>
            <span className="abbr">
              <FilterList />
            </span>
          </Button>
          <DropDown
            show={showFilterDropDown}
            onPress={() => {
              const filters: UserFilterParams = { ...userFilterParams, sortBy: '', sortDir: undefined };
              setWithFilter(true);
              loadData(1, filters);
              setShowFilterDropDown(false);
            }}
          >
            <>
              <section className="Filter--section">
                <p>{t('last-connection')}</p>
                <TextField
                  margin="dense"
                  fullWidth
                  name="pickupDate"
                  label={t('from')}
                  type="datetime-local"
                  onChange={(event: any) => {
                    setUserFilterParams({ ...userFilterParams, fromDate: parseDateTimeLumen(event.target.value) });
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  margin="dense"
                  fullWidth
                  name="dropoffDate"
                  label={t('to')}
                  type="datetime-local"
                  onChange={(event: any) => {
                    setUserFilterParams({ ...userFilterParams, toDate: parseDateTimeLumen(event.target.value) });
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </section>
              <section className="Search--section">
                <p>{t('categories')}</p>
                <FormControl fullWidth>
                  <Select
                    className="Select--filter"
                    variant="outlined"
                    onChange={(event: any) => {
                      setUserFilterParams({ ...userFilterParams, searchBy: event.target.value });
                    }}
                    defaultValue={0}
                  >
                    <MenuItem value={0}>{t('all-categories')}</MenuItem>
                    {userMenuItems.map(({ value, label }, index) => (
                      <MenuItem key={index} value={`${value}`}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </section>
            </>
          </DropDown>
          <SearchBar
            menuItems={userMenuItems}
            handleSelectChange={(event) => {
              setUserFilterParams({ ...userFilterParams, searchBy: event.target.value });
            }}
            handleInputChange={(event) => {
              setUserFilterParams({ ...userFilterParams, search: event.target.value.toLocaleLowerCase() });
            }}
            onPress={() => loadData(1, userFilterParams)}
            disabled={isLoading}
          />
          <Button size="small" variant="contained" color="primary" className="Filter--button" onClick={getXlsxUsers}>
            <GetApp />
            <p>{t('export')}</p>
          </Button>
        </div>
      </div>
      <TableContainer component={Paper}>
        <Table className="Mui--table">
          <TableHead>
            <TableRow>
              <TableCell className="Mui--tablecell" onClick={() => handleTableCellClick('fullname')}>
                <div className="Table--cell">
                  <p>{t('name')}</p>
                  <ArrowUpward
                    className={`Arrow--icon ${sortedField.field === 'fullname' ? `sortedBy ${sortedField.sort}` : ''}`}
                  />
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell">
                <div className="Table--cell">
                  <p>{t('upper-email')}</p>
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell" onClick={() => handleTableCellClick('origin_address')}>
                <div className="Table--cell">
                  <p>{t('form-schemas.identification-number')}</p>
                  <ArrowUpward
                    className={`Arrow--icon ${
                      sortedField.field === 'origin_address' ? `sortedBy ${sortedField.sort}` : ''
                    }`}
                  />
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell">
                <div className="Table--cell">
                  <p>{t('form-schemas.upper-age-range')}</p>
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell">
                <div className="Table--cell">
                  <p>{t('form-schemas.upper-phone')}</p>
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell">
                <div className="Table--cell">
                  <p>{t('venue-code')}</p>
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell">
                <div className="Table--cell">
                  <p>UE MAC</p>
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell">
                <div className="Table--cell">
                  <p>UE IP</p>
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell">
                <div className="Table--cell">
                  <p>{t('seller-name')}</p>
                </div>
              </TableCell>
              <TableCell className="Mui--tablecell">
                <div className="Table--cell">
                  <p>{t('components.actions')}</p>
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.data ? (
              renderContent(users?.data)
            ) : (
              <TableRow>
                <TableCell colSpan={7} component="th" scope="row">
                  {t('components.no-data')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {users && (
        <TablePagination
          component="div"
          count={users.total}
          page={users.currentPage - 1}
          onPageChange={(_, page: number) => {
            loadData(page + 1, userFilterParams);
          }}
          rowsPerPage={users.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from} - ${to} de ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
